import { useMemo } from "react";
import { Sparkles } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { filterBottomNavShortcuts, type NavItem } from "../../config/navigation";
import { useAuth } from "../../stores/auth";
import { useBottomBarActionsSnapshot } from "../../stores/bottom-bar-actions-store";
import { useShellBottomBar, type BottomBarAction } from "../../stores/shell-bottom-bar";
import { useShellTabs } from "../../stores/shell-tabs";
import { Icon } from "../icons";

function ShortcutButton({
  item,
  active,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={item.label}
      className={`flex w-[4.25rem] shrink-0 flex-col items-center gap-1 rounded-lg px-1 py-1.5 transition-colors ${
        active
          ? "bg-brand-pink/15 text-brand-pink"
          : "text-muted hover:bg-hover hover:text-foreground"
      }`}
    >
      <Icon icon={item.icon} size="md" className={active ? "text-brand-pink" : undefined} />
      <span className="max-w-full truncate text-[10px] leading-tight">{item.label}</span>
    </button>
  );
}

function ActionButton({ action }: { action: BottomBarAction }) {
  const variant = action.variant ?? "secondary";
  const className =
    variant === "primary"
      ? "bg-gradient-brand text-white shadow-sm shadow-brand-pink/20 hover:opacity-95"
      : variant === "danger"
        ? "border border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20"
        : "border border-line bg-input-bg text-foreground hover:bg-hover";

  return (
    <button
      type="button"
      disabled={action.disabled}
      onClick={action.onClick}
      className={`inline-flex h-8 shrink-0 flex-row items-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
    >
      {action.icon && <Icon icon={action.icon} size="sm" className="shrink-0" />}
      <span className="leading-none">{action.label}</span>
    </button>
  );
}

function isNavActive(item: NavItem, locationPath: string): boolean {
  if (item.to === "/") return locationPath === "/";
  if (item.to.startsWith("/admin")) return locationPath.startsWith("/admin");
  return locationPath === item.to || locationPath.startsWith(`${item.to}/`);
}

export function BottomNavBar() {
  const { can, moduleEnabled } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const openInActiveTab = useShellTabs((s) => s.openInActiveTab);
  const actions = useBottomBarActionsSnapshot();
  const setCopilotOpen = useShellBottomBar((s) => s.setCopilotOpen);
  const showCopilot = can("ai.copilot") && moduleEnabled("ai_copilot_bo");

  const shortcuts = useMemo(
    () => filterBottomNavShortcuts(can, moduleEnabled),
    [can, moduleEnabled],
  );

  const handleNavigate = (path: string) => {
    navigate(openInActiveTab(path));
  };

  return (
    <nav
      className="flex w-full shrink-0 border-t border-line bg-surface"
      style={{ height: "var(--bottom-nav-height)" }}
      aria-label="Bottom navigation"
    >
      <div className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto px-2 py-1">
        {shortcuts.map((item) => (
          <ShortcutButton
            key={item.to}
            item={item}
            active={isNavActive(item, location.pathname)}
            onClick={() => handleNavigate(item.to)}
          />
        ))}
      </div>

      <div className="flex shrink-0 items-center gap-2 border-l border-line px-3 py-1.5">
        {showCopilot && (
          <button
            type="button"
            onClick={() => setCopilotOpen(true)}
            title="Search or ask AI"
            aria-label="Search or ask AI"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-brand text-white shadow-sm shadow-brand-pink/25 transition-opacity hover:opacity-95"
          >
            <Sparkles size={16} aria-hidden />
          </button>
        )}
        {actions.map((action) => (
          <ActionButton key={action.id} action={action} />
        ))}
      </div>
    </nav>
  );
}
